import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../config/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine
} from "recharts";
import { RefreshCw, Activity, TrendingUp, Clock } from "lucide-react";
import FreshnessDot from "./FreshnessDot";
import { enrichWithAnchoredVwap } from "../utils/vwap";
import { usePremarketLevels } from "../hooks/usePremarketLevels";

const StockVixChart = ({ ticker, timeframe = '1d' }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [currentValues, setCurrentValues] = useState({
    price: null,
    vix: null
  });
  // PM H/L white dotted reference lines (intraday only)
  const { ready: pmReady, premarketHigh, premarketLow } = usePremarketLevels(ticker);

  const fetchVixData = useCallback(async () => {
    if (!ticker) return;
    const LS_KEY = `sp_vix_${ticker}_${timeframe}`;

    // Hydrate from cache instantly
    let hadCache = false;
    try {
      const cached = JSON.parse(localStorage.getItem(LS_KEY) || "null");
      if (cached?.data?.chart_data) {
        setChartData(cached.data.chart_data);
        setCurrentValues(cached.data.current_values || {});
        setLastUpdate(new Date(cached.ts));
        hadCache = true;
      }
    } catch {}

    if (!hadCache) setLoading(true);
    setError(null);

    const TIMEOUTS = [45000, 60000, 90000];
    let lastErr = null;
    for (let attempt = 0; attempt < TIMEOUTS.length; attempt++) {
      try {
        const response = await axios.get(`${API}/stock/vix-chart/${ticker}`, {
          params: { timeframe },
          timeout: TIMEOUTS[attempt],
        });
        if (response.data && response.data.chart_data) {
          setChartData(response.data.chart_data);
          setCurrentValues(response.data.current_values || {});
          setLastUpdate(new Date());
          try { localStorage.setItem(LS_KEY, JSON.stringify({ data: response.data, ts: Date.now() })); } catch {}
          setLoading(false);
          return;
        } else if (response.data?.error) {
          setError(response.data.error);
          setLoading(false);
          return;
        }
      } catch (err) {
        lastErr = err;
        if (attempt < TIMEOUTS.length - 1) {
          await new Promise(r => setTimeout(r, (attempt + 1) * 1500));
        }
      }
    }
    console.warn(`VIX ${ticker}: silent fail, keeping cached data`, lastErr?.message);
    if (!hadCache) setError("Failed to load VIX data. Please try again.");
    setLoading(false);
  }, [ticker, timeframe]);

  useEffect(() => {
    fetchVixData();
    const interval = setInterval(fetchVixData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchVixData]);

  const getVixSignal = (value) => {
    if (value >= 30) return { text: "EXTREME FEAR", color: "#ef4444" };
    if (value >= 20) return { text: "HIGH FEAR", color: "#f97316" };
    if (value >= 15) return { text: "MODERATE", color: "#eab308" };
    return { text: "LOW FEAR", color: "#22c55e" };
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-3 shadow-xl">
          <p className="text-gray-400 text-xs mb-2">{label}</p>
          <div className="space-y-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-gray-300">{entry.name}:</span>
                <span className="font-bold" style={{ color: entry.color }}>
                  {(entry.name === 'Price' || entry.name === 'VWAP (anchored)') ? `$${entry.value?.toFixed(2)}` : entry.value?.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const vixSignal = currentValues.vix ? getVixSignal(currentValues.vix) : null;

  // Compute per-bar Anchored VWAP for the period
  const enrichedChartData = useMemo(() => enrichWithAnchoredVwap(chartData), [chartData]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-orange-400" />
          <h3 className="text-lg font-semibold text-white">{ticker} VIX & Price</h3>
          <span className="text-xs text-gray-500">Market Volatility Index</span>
          <FreshnessDot timestamp={lastUpdate} size="sm" showLabel={true} />
        </div>
        <div className="flex items-center gap-2">
          {lastUpdate && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchVixData}
            disabled={loading}
            className="flex items-center gap-1 px-2 py-1 bg-orange-600 hover:bg-orange-700 rounded text-xs text-white transition-colors"
            data-testid="refresh-stock-vix"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Current Values Cards */}
      <div className="grid grid-cols-2 gap-3">
        {/* Price Card */}
        <div className="bg-gradient-to-br from-purple-900/40 to-[#1a1a2e] border border-purple-700/50 rounded-lg p-4" data-testid="vix-price-card">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-purple-400" />
            <span className="text-xs text-purple-400 font-medium">PRICE</span>
          </div>
          <div className="text-2xl font-bold text-purple-400">
            ${currentValues.price?.toFixed(2) || '--'}
          </div>
        </div>

        {/* VIX Card */}
        <div className="bg-gradient-to-br from-orange-900/40 to-[#1a1a2e] border border-orange-700/50 rounded-lg p-4" data-testid="vix-value-card">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-orange-400" />
            <span className="text-xs text-orange-400 font-medium">VIX</span>
          </div>
          <div className="text-2xl font-bold" style={{ color: vixSignal?.color || '#6b7280' }}>
            {currentValues.vix?.toFixed(2) || '--'}
          </div>
          <div className="text-xs mt-1" style={{ color: vixSignal?.color || '#6b7280' }}>
            {vixSignal?.text || 'Loading...'}
          </div>
        </div>
      </div>

      {/* VIX + Price Chart */}
      <div className="bg-[#111] border border-gray-800 rounded-lg p-3" data-testid="stock-vix-chart">
        {loading && chartData.length === 0 ? (
          <div className="h-[350px] flex items-center justify-center">
            <div className="text-center">
              <RefreshCw className="w-6 h-6 animate-spin text-orange-500 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">Loading VIX data...</p>
            </div>
          </div>
        ) : error ? (
          <div className="h-[350px] flex items-center justify-center" data-testid="vix-skeleton">
            <div className="text-center max-w-sm">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400 mb-2" />
              <p className="text-sm text-amber-300" data-testid="vix-unavailable">VIX data temporarily unavailable</p>
              <p className="text-[11px] text-gray-500 mt-1">{error}</p>
              <button data-testid="vix-retry" onClick={() => fetchVixData()} className="mt-3 px-3 py-1 rounded border border-gray-700 text-xs text-gray-300 hover:text-white hover:border-gray-500">Try again</button>
            </div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[350px] flex items-center justify-center">
            <p className="text-gray-500 text-sm">No data available</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={enrichedChartData} margin={{ top: 20, right: 60, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis 
                dataKey="time" 
                stroke="#666" 
                tick={{ fill: '#888', fontSize: 10 }}
                tickLine={{ stroke: '#444' }}
              />
              {/* Price Y-Axis (Left) */}
              <YAxis 
                yAxisId="price"
                domain={['auto', 'auto']}
                stroke="#666"
                tick={{ fill: '#888', fontSize: 10 }}
                tickLine={{ stroke: '#444' }}
                tickFormatter={(value) => `$${value.toFixed(0)}`}
                label={{ value: 'Price ($)', angle: -90, position: 'insideLeft', fill: '#a855f7', fontSize: 10 }}
              />
              {/* VIX Y-Axis (Right) */}
              <YAxis 
                yAxisId="vix"
                orientation="right"
                domain={['auto', 'auto']}
                stroke="#666"
                tick={{ fill: '#888', fontSize: 10 }}
                tickLine={{ stroke: '#444' }}
                label={{ value: 'VIX Level', angle: 90, position: 'insideRight', fill: '#f97316', fontSize: 10 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ paddingTop: '10px' }} 
                iconType="circle" 
              />
              
              {/* Anchored VWAP — violet per-bar line, accumulates from period start */}
              <Line
                yAxisId="price"
                type="monotone"
                dataKey="vwap"
                name="VWAP (anchored)"
                stroke="#ee82ee"
                strokeWidth={2.5}
                strokeDasharray="4 3"
                dot={false}
                connectNulls
                isAnimationActive={false}
              />

              {/* Reference lines for VIX levels */}
              <ReferenceLine yAxisId="vix" y={30} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'Fear (30)', fill: '#ef4444', fontSize: 9, position: 'right' }} />
              <ReferenceLine yAxisId="vix" y={20} stroke="#f97316" strokeDasharray="5 5" label={{ value: 'High (20)', fill: '#f97316', fontSize: 9, position: 'right' }} />
              <ReferenceLine yAxisId="vix" y={15} stroke="#eab308" strokeDasharray="5 5" />

              {/* Premarket High / Low — white dotted lines on the price axis
                  so intraday traders see current price vs PM range. Only
                  rendered on the 1d intraday view. */}
              {pmReady && timeframe === '1d' && premarketHigh != null && (
                <ReferenceLine yAxisId="price" y={premarketHigh} stroke="#ffffff"
                  strokeDasharray="2 4" strokeWidth={1.5} strokeOpacity={0.9}
                  ifOverflow="extendDomain"
                  label={{ value: `PM H $${premarketHigh}`, fill: '#ffffff', fontSize: 10, position: 'right' }} />
              )}
              {pmReady && timeframe === '1d' && premarketLow != null && (
                <ReferenceLine yAxisId="price" y={premarketLow} stroke="#ffffff"
                  strokeDasharray="2 4" strokeWidth={1.5} strokeOpacity={0.9}
                  ifOverflow="extendDomain"
                  label={{ value: `PM L $${premarketLow}`, fill: '#ffffff', fontSize: 10, position: 'right' }} />
              )}
              
              {/* Price Line - Purple */}
              <Line
                yAxisId="price"
                type="monotone"
                dataKey="price"
                name="Price"
                stroke="#a855f7"
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6, fill: '#a855f7' }}
              />
              
              {/* VIX Line - Orange (matching Market Indicators page) */}
              <Line
                yAxisId="vix"
                type="monotone"
                dataKey="vix"
                name="VIX"
                stroke="#f97316"
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6, fill: '#f97316' }}
                connectNulls={true}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Info */}
      <div className="bg-[#111] border border-gray-800 rounded p-3 text-xs text-gray-500">
        <p className="font-medium text-orange-400 mb-1">VIX (CBOE Volatility Index)</p>
        <p>Measures expected 30-day volatility of the S&P 500. Higher VIX = more fear in markets. Values above 20 indicate elevated volatility, above 30 indicates extreme fear.</p>
      </div>
    </div>
  );
};

export default StockVixChart;
